/**
 * Welcome to the main entry point of the app.
 *
 * Most of this file is boilerplate and you shouldn't need to modify
 * it very often. But take some time to look through and understand
 * what is going on here.
 *
 * The app navigation resides in ./app/navigation, so head over there
 * if you're interested in adding screens and navigators.
 */
import { initializeAmplify } from '@akin-home/amplify';
import { AuthProvider } from '@akin-home/auth/react';
import { HabitatProvider } from '@akin-home/habitats/react';
import { PortalProvider } from '@gorhom/portal';
import { NavigationContainer } from '@react-navigation/native';
import { decode, encode } from 'base-64';
import { useKeepAwake } from 'expo-keep-awake';
import React from 'react';
import { LogBox } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { RootSiblingParent } from 'react-native-root-siblings';
import {
  SafeAreaProvider,
  initialWindowMetrics,
} from 'react-native-safe-area-context';
import { enableScreens } from 'react-native-screens';
import { QueryClientProvider } from 'react-query';
import LockScreenProvider from './auth/lock_screen/components/LockScreenProvider';
import { EventOccurenceProvider } from './calendar/components/EventOccurenceProvider';
import { AkinChatProvider } from './chat/useAkinChat';
import './components/Avatar/suppressWarnings';
import FullScreenLoader from './components/FullScreenLoader';
import ToastRoot from './components/Toast/ToastRoot';
import { removeUserCredentials } from './credentialStorage';
import { RootStackNavigator } from './navigation/RootStackNavigator/RootStackNavigator';
import { query_client } from './query_client';
import { RobotProvider } from './robots/RobotProvider';
import { PreAuthScreen } from './screens/LoginScreen/LoginScreen';
import { useSpeech } from './speech-to-text/useSpeech';
import { useInitTTS } from './text-to-speech/useInitTTS';
import { NavigationTheme } from './theme/themes';
import { CurrentUserProvider } from './users/components/CurrentProfileProvider';
import { loadFonts } from './utils/fontAwesome';

LogBox.ignoreLogs([
  'Setting a timer',
  'Invalid prop `config.position` of type `string` supplied to `Toast`',
]);

if (!global.btoa) {
  global.btoa = encode;
}
if (!global.atob) {
  global.atob = decode;
}

initializeAmplify();

loadFonts();
enableScreens();

const gesture_style = { flex: 1 };

/** All of our main app context providers. Yes it's a bit scary but it works well */
const App = () => {
  useKeepAwake(); // Prevents the app from going to sleep.
  useSpeech(); // Initialised speech capabilities
  useInitTTS();

  return (
    <SafeAreaProvider initialMetrics={initialWindowMetrics}>
      <GestureHandlerRootView style={gesture_style}>
        <NavigationContainer theme={NavigationTheme}>
          <AuthProvider
            onSignOut={removeUserCredentials}
            unauthenticated_view={<PreAuthScreen />}
            loading_view={<FullScreenLoader />}
          >
            <QueryClientProvider client={query_client}>
              {/* Contains habitat and user data */}
              <HabitatProvider>
                <CurrentUserProvider>
                  <PortalProvider>
                    {/* Contains chat api integration */}
                    <AkinChatProvider>
                      <RobotProvider>
                        <EventOccurenceProvider>
                          <LockScreenProvider>
                            <RootSiblingParent>
                              <RootStackNavigator />
                            </RootSiblingParent>
                          </LockScreenProvider>
                        </EventOccurenceProvider>
                      </RobotProvider>
                    </AkinChatProvider>
                  </PortalProvider>
                </CurrentUserProvider>
              </HabitatProvider>
            </QueryClientProvider>
          </AuthProvider>
        </NavigationContainer>
        <ToastRoot />
      </GestureHandlerRootView>
    </SafeAreaProvider>
  );
};

export default App;
